import { useCallback, useEffect, useMemo, useState, ReactNode, FormEvent } from 'react'
import Card from '@/components/ui/Card'
import { useAuth } from '@/lib/auth-context'
import { supabase } from '@/supabase/client'

interface EmptyStateProps {
  title: string
  description: string
  action?: ReactNode
}

interface OrderItem {
  name: string
  category: string
  qty: number
  amount: string
}

interface RequestItem {
  description: string
  amount: string
  receipt_url: string
}

interface ItemRowProps {
  index: number
  item: OrderItem
  onChange: (index: number, item: OrderItem) => void
  onRemove: (index: number) => void
}

interface RequestItemRowProps {
  index: number
  item: RequestItem
  onChange: (index: number, item: RequestItem) => void
  onRemove: (index: number) => void
}

interface BudgetData {
  budget_id: string
  teacher_id: string
  fiscal_year: number
  allocated_cents: number
  spent_cents: number
  locked: boolean
  created_at: string | null
}

interface Order {
  id: string
  status: string
  placed_at: string | null
  total_cents: number
}

interface ReimbursementRequest {
  id: string
  status: string
  submitted_at: string | null
  total_cents: number
}

function formatCurrency(cents: number | null | undefined): string {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100)
}

function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-sand p-10 text-center text-cocoa/70">
      <h3 className="text-lg font-semibold text-cocoa">{title}</h3>
      <p className="max-w-md text-sm">{description}</p>
      {action}
    </div>
  )
}

function ItemRow({ index, item, onChange, onRemove }: ItemRowProps) {
  return (
    <div className="grid gap-3 md:grid-cols-[2fr_1fr_auto_auto]">
      <input
        type="text"
        value={item.name}
        onChange={(event) => onChange(index, { ...item, name: event.target.value })}
        placeholder="Item name"
        className="w-full rounded-lg border border-sand px-3 py-2 text-sm"
      />
      <input
        type="text"
        value={item.category}
        onChange={(event) => onChange(index, { ...item, category: event.target.value })}
        placeholder="Category"
        className="w-full rounded-lg border border-sand px-3 py-2 text-sm"
      />
      <input
        type="number"
        min="1"
        value={item.qty}
        onChange={(event) => onChange(index, { ...item, qty: Number(event.target.value || 1) })}
        className="w-full rounded-lg border border-sand px-3 py-2 text-sm"
      />
      <input
        type="number"
        min="0"
        step="0.01"
        value={item.amount}
        onChange={(event) => onChange(index, { ...item, amount: event.target.value })}
        placeholder="Line total ($)"
        className="w-full rounded-lg border border-sand px-3 py-2 text-sm"
      />
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="rounded-lg border border-brick px-3 py-2 text-sm font-medium text-brick hover:bg-brick/10"
      >
        Remove
      </button>
    </div>
  )
}

function RequestItemRow({ index, item, onChange, onRemove }: RequestItemRowProps) {
  return (
    <div className="grid gap-3 md:grid-cols-[2fr_auto_auto]">
      <div className="space-y-2">
        <input
          type="text"
          value={item.description}
          onChange={(event) => onChange(index, { ...item, description: event.target.value })}
          placeholder="Description"
          className="w-full rounded-lg border border-sand px-3 py-2 text-sm"
        />
        <input
          type="url"
          value={item.receipt_url}
          onChange={(event) => onChange(index, { ...item, receipt_url: event.target.value })}
          placeholder="Receipt URL (optional)"
          className="w-full rounded-lg border border-sand px-3 py-2 text-sm"
        />
      </div>
      <input
        type="number"
        min="0"
        step="0.01"
        value={item.amount}
        onChange={(event) => onChange(index, { ...item, amount: event.target.value })}
        placeholder="Amount ($)"
        className="w-full rounded-lg border border-sand px-3 py-2 text-sm"
      />
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="rounded-lg border border-brick px-3 py-2 text-sm font-medium text-brick hover:bg-brick/10"
      >
        Remove
      </button>
    </div>
  )
}

export default function TeacherBudget() {
  const { user, session } = useAuth()
  const [budgets, setBudgets] = useState<BudgetData[]>([])
  const [loadingBudgets, setLoadingBudgets] = useState(true)
  const [activeBudgetId, setActiveBudgetId] = useState<string | null>(null)
  const [orders, setOrders] = useState<Order[]>([])
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [orderError, setOrderError] = useState('')
  const [showOrderForm, setShowOrderForm] = useState(false)
  const [orderItems, setOrderItems] = useState<OrderItem[]>([{ name: '', category: '', qty: 1, amount: '' }])
  const [submittingOrder, setSubmittingOrder] = useState(false)

  const [requests, setRequests] = useState<ReimbursementRequest[]>([])
  const [requestsLoading, setRequestsLoading] = useState(false)
  const [requestError, setRequestError] = useState('')
  const [showRequestForm, setShowRequestForm] = useState(false)
  const [requestItems, setRequestItems] = useState<RequestItem[]>([{ description: '', amount: '', receipt_url: '' }])
  const [requestMemo, setRequestMemo] = useState('')
  const [submittingRequest, setSubmittingRequest] = useState(false)

  const bearer = session?.access_token

  const selectedBudget = useMemo(() => {
    if (!budgets.length) return null
    if (activeBudgetId) {
      const found = budgets.find((budget) => budget.budget_id === activeBudgetId)
      if (found) return found
    }
    return budgets[0]
  }, [budgets, activeBudgetId])

  const loadBudgetData = useCallback(async () => {
    if (!user?.id) return []

    const { data: usageData, error: usageError } = await (supabase as any)
      .from('v_budget_usage')
      .select('budget_id, teacher_id, fiscal_year, allocated_cents, spent_cents')
      .eq('teacher_id', user.id)
      .order('fiscal_year', { ascending: false })

    if (usageError) throw usageError

    const typedUsageData = usageData as unknown as Array<{ budget_id: string; teacher_id: string; fiscal_year: number; allocated_cents: number; spent_cents: number }> | null
    const ids = typedUsageData?.map((row) => row.budget_id).filter(Boolean) || []
    let lockedLookup: Record<string, { id: string; locked: boolean; created_at: string | null }> = {}
    if (ids.length) {
      const { data: locks } = await supabase
        .from('teacher_budgets')
        .select('id, locked, created_at')
        .in('id', ids)
      lockedLookup = Object.fromEntries((locks || []).map((row) => [row.id, row]))
    }

    return (typedUsageData || []).map((row) => ({
      ...row,
      locked: lockedLookup[row.budget_id]?.locked ?? false,
      created_at: lockedLookup[row.budget_id]?.created_at ?? null,
    })) as BudgetData[]
  }, [user?.id])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setLoadingBudgets(true)
      try {
        const merged = await loadBudgetData()
        if (!cancelled) {
          setBudgets(merged)
        }
      } catch (err) {
        console.error('budget usage error', err)
        if (!cancelled) {
          setBudgets([])
        }
      } finally {
        if (!cancelled) {
          setLoadingBudgets(false)
        }
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [loadBudgetData])

  useEffect(() => {
    if (!activeBudgetId && budgets.length) {
      setActiveBudgetId(budgets[0].budget_id)
    }
  }, [activeBudgetId, budgets])

  const refreshBudgets = useCallback(async () => {
    setLoadingBudgets(true)
    try {
      const merged = await loadBudgetData()
      setBudgets(merged)
    } catch (err) {
      console.error('budget usage error', err)
      setBudgets([])
    } finally {
      setLoadingBudgets(false)
    }
  }, [loadBudgetData])

  const fetchOrders = async () => {
    if (!bearer) return
    setOrdersLoading(true)
    setOrderError('')
    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
      const response = await fetch(`${CORE_API}/api/v1/orders`, {
        headers: { Authorization: `Bearer ${bearer}` },
      })
      const raw = await response.json().catch(() => ({}))
      if (!response.ok) {
        const errMsg = typeof raw?.error === 'string' ? raw.error : raw?.error?.message
        throw new Error(errMsg || 'Unable to load orders')
      }
      const payload = raw?.data ?? raw
      setOrders(payload.items || [])
    } catch (err) {
      setOrderError(err instanceof Error ? err.message : 'An error occurred')
      setOrders([])
    } finally {
      setOrdersLoading(false)
    }
  }

  const fetchRequests = async () => {
    if (!bearer) return
    setRequestsLoading(true)
    setRequestError('')
    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
      const response = await fetch(`${CORE_API}/api/v1/teacher/reimbursements`, {
        headers: { Authorization: `Bearer ${bearer}` },
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to load reimbursements')
      }
      setRequests(payload.items || [])
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : 'An error occurred')
      setRequests([])
    } finally {
      setRequestsLoading(false)
    }
  }

  useEffect(() => {
    if (bearer) {
      fetchOrders()
      fetchRequests()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bearer])

  const totalOrderAmount = useMemo(() => {
    return orderItems.reduce((sum, item) => {
      const dollars = Number.parseFloat(item.amount)
      return sum + (Number.isFinite(dollars) ? Math.round(dollars * 100) : 0)
    }, 0)
  }, [orderItems])

  const totalRequestAmount = useMemo(() => {
    return requestItems.reduce((sum, item) => {
      const dollars = Number.parseFloat(item.amount)
      return sum + (Number.isFinite(dollars) ? Math.round(dollars * 100) : 0)
    }, 0)
  }, [requestItems])

  const handleOrderItemChange = (index: number, next: OrderItem) => {
    setOrderItems((prev) => prev.map((item, idx) => (idx === index ? next : item)))
  }

  const handleOrderItemRemove = (index: number) => {
    setOrderItems((prev) => prev.filter((_, idx) => idx !== index))
  }

  const handleRequestItemChange = (index: number, next: RequestItem) => {
    setRequestItems((prev) => prev.map((item, idx) => (idx === index ? next : item)))
  }

  const handleRequestItemRemove = (index: number) => {
    setRequestItems((prev) => prev.filter((_, idx) => idx !== index))
  }

  const resetOrderForm = () => {
    setOrderItems([{ name: '', category: '', qty: 1, amount: '' }])
    setShowOrderForm(false)
  }

  const resetRequestForm = () => {
    setRequestItems([{ description: '', amount: '', receipt_url: '' }])
    setRequestMemo('')
    setShowRequestForm(false)
  }

  const submitOrder = async (event: FormEvent) => {
    event.preventDefault()
    if (!bearer) return
    if (!selectedBudget) return
    setSubmittingOrder(true)
    try {
      const items = orderItems
        .map((item) => {
          const amount = Number.parseFloat(item.amount)
          return {
            name: item.name,
            category: item.category,
            qty: item.qty,
            amount_cents: Number.isFinite(amount) ? Math.round(amount * 100) : 0,
          }
        })
        .filter((item) => item.name && item.amount_cents > 0)

      if (!items.length) {
        throw new Error('Add at least one valid line item')
      }

      const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
      const response = await fetch(`${CORE_API}/api/v1/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bearer}`,
        },
        body: JSON.stringify({ budget_id: selectedBudget.budget_id, items }),
      })

      const raw = await response.json().catch(() => ({}))
      if (!response.ok) {
        const errMsg = typeof raw?.error === 'string' ? raw.error : raw?.error?.message
        throw new Error(errMsg || 'Unable to submit order')
      }
      resetOrderForm()
      fetchOrders()
      await refreshBudgets()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setSubmittingOrder(false)
    }
  }

  const submitRequest = async (event: FormEvent) => {
    event.preventDefault()
    if (!bearer) return
    setSubmittingRequest(true)
    try {
      const items = requestItems
        .map((item) => {
          const amount = Number.parseFloat(item.amount)
          return {
            description: item.description,
            receipt_url: item.receipt_url || null,
            amount_cents: Number.isFinite(amount) ? Math.round(amount * 100) : 0,
          }
        })
        .filter((item) => item.description && item.amount_cents > 0)

      if (!items.length) {
        throw new Error('Add at least one reimbursement line item')
      }

      const CORE_API_SUBMIT = import.meta.env.VITE_CORE_API_URL || '';
      const response = await fetch(`${CORE_API_SUBMIT}/api/v1/teacher/reimbursements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bearer}`,
        },
        body: JSON.stringify({
          budget_id: selectedBudget?.budget_id || null,
          memo: requestMemo || null,
          items,
        }),
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to submit reimbursement')
      }
      resetRequestForm()
      fetchRequests()
      await refreshBudgets()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setSubmittingRequest(false)
    }
  }

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-cocoa">Budget &amp; Purchases</h1>
        <p className="text-sm text-cocoa/70">Review your allocation, submit new orders, and manage reimbursements.</p>
      </header>

      <Card className="space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h2 className="text-lg font-semibold text-cocoa">Current Allocation</h2>
        </div>
        {loadingBudgets ? (
          <p className="text-sm text-cocoa/70">Loading budget…</p>
        ) : budgets.length ? (
          <div className="grid gap-4 md:grid-cols-3">
            {budgets.map((budget) => {
              const remaining = (budget.allocated_cents || 0) - (budget.spent_cents || 0)
              return (
                <div key={budget.budget_id} className="rounded-2xl border border-sand p-4">
                  <p className="text-xs uppercase tracking-wide text-cocoa/60">Fiscal Year {budget.fiscal_year}</p>
                  <p className="mt-2 text-lg font-semibold text-cocoa">{formatCurrency(budget.allocated_cents)}</p>
                  <dl className="mt-3 space-y-1 text-sm text-cocoa/80">
                    <div className="flex items-center justify-between">
                      <dt>Spent</dt>
                      <dd>{formatCurrency(budget.spent_cents)}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt>Remaining</dt>
                      <dd className={remaining < 0 ? 'text-brick font-semibold' : ''}>{formatCurrency(remaining)}</dd>
                    </div>
                  </dl>
                  {budget.locked ? (
                    <p className="mt-3 rounded-full bg-cocoa/5 px-3 py-1 text-xs font-medium uppercase tracking-wide text-cocoa/70">
                      Locked
                    </p>
                  ) : null}
                </div>
              )
            })}
          </div>
        ) : (
          <EmptyState
            title="No budget yet"
            description="Once an administrator assigns a classroom allocation it will appear here."
          />
        )}
      </Card>

      <Card className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h2 className="text-lg font-semibold text-cocoa">Orders</h2>
          <button
            type="button"
            disabled={!selectedBudget}
            onClick={() => setShowOrderForm(true)}
            className="rounded-full bg-brick px-4 py-2 text-sm font-semibold text-white shadow hover:bg-brick/90 disabled:opacity-60"
          >
            + New Order
          </button>
        </div>
        {budgets.length > 1 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sand bg-shell/40 px-3 py-2 text-xs uppercase tracking-wide text-cocoa/60">
            <span>Working budget</span>
            <select
              value={selectedBudget?.budget_id || ''}
              onChange={(event) => setActiveBudgetId(event.target.value || null)}
              className="rounded-lg border border-sand bg-white px-3 py-1 text-sm text-cocoa"
            >
              {budgets.map((budget) => (
                <option key={budget.budget_id} value={budget.budget_id}>
                  {budget.fiscal_year} · {formatCurrency(budget.allocated_cents)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {showOrderForm ? (
          <form onSubmit={submitOrder} className="space-y-4 rounded-2xl border border-sand p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60">Order Details</h3>
            <div className="space-y-3">
              {orderItems.map((item, idx) => (
                <ItemRow
                  key={`order-item-${idx}`}
                  index={idx}
                  item={item}
                  onChange={handleOrderItemChange}
                  onRemove={handleOrderItemRemove}
                />
              ))}
              <button
                type="button"
                onClick={() => setOrderItems((prev) => [...prev, { name: '', category: '', qty: 1, amount: '' }])}
                className="rounded-lg border border-sand px-3 py-2 text-sm text-cocoa hover:bg-shell"
              >
                Add line
              </button>
            </div>
            <div className="flex items-center justify-between text-sm text-cocoa/80">
              <span>Total</span>
              <span className="font-semibold text-cocoa">{formatCurrency(totalOrderAmount)}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={submittingOrder}
                className="rounded-full bg-brick px-5 py-2 text-sm font-semibold text-white hover:bg-brick/90 disabled:opacity-60"
              >
                {submittingOrder ? 'Submitting…' : 'Submit order'}
              </button>
              <button
                type="button"
                onClick={resetOrderForm}
                className="rounded-full border border-sand px-5 py-2 text-sm font-semibold text-cocoa hover:bg-shell"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {ordersLoading ? (
          <p className="text-sm text-cocoa/70">Loading orders…</p>
        ) : orderError ? (
          <p className="text-sm text-brick">{orderError}</p>
        ) : orders.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-sand">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-cocoa/60">
                  <th className="px-3 py-2">Order</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Placed</th>
                  <th className="px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand text-sm text-cocoa/80">
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td className="px-3 py-2 font-mono text-xs">{order.id}</td>
                    <td className="px-3 py-2 capitalize">{order.status || 'pending'}</td>
                    <td className="px-3 py-2">{order.placed_at ? new Date(order.placed_at).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2 font-semibold text-cocoa">{formatCurrency(order.total_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No orders yet"
            description="Track shopping orders once you submit them for review."
            action={
              <button
                type="button"
                onClick={() => setShowOrderForm(true)}
                className="rounded-full border border-sand px-5 py-2 text-sm font-semibold text-cocoa hover:bg-shell"
              >
                Create your first order
              </button>
            }
          />
        )}
      </Card>

      <Card className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h2 className="text-lg font-semibold text-cocoa">Reimbursements</h2>
          <button
            type="button"
            disabled={!selectedBudget}
            onClick={() => setShowRequestForm(true)}
            className="rounded-full bg-cocoa px-4 py-2 text-sm font-semibold text-white shadow hover:bg-cocoa/90 disabled:opacity-60"
          >
            + New reimbursement
          </button>
        </div>
        {budgets.length > 1 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sand bg-shell/40 px-3 py-2 text-xs uppercase tracking-wide text-cocoa/60">
            <span>Working budget</span>
            <select
              value={selectedBudget?.budget_id || ''}
              onChange={(event) => setActiveBudgetId(event.target.value || null)}
              className="rounded-lg border border-sand bg-white px-3 py-1 text-sm text-cocoa"
            >
              {budgets.map((budget) => (
                <option key={budget.budget_id} value={budget.budget_id}>
                  {budget.fiscal_year} · {formatCurrency(budget.allocated_cents)}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {showRequestForm ? (
          <form onSubmit={submitRequest} className="space-y-4 rounded-2xl border border-sand p-4">
            <div className="space-y-3">
              {requestItems.map((item, idx) => (
                <RequestItemRow
                  key={`request-item-${idx}`}
                  index={idx}
                  item={item}
                  onChange={handleRequestItemChange}
                  onRemove={handleRequestItemRemove}
                />
              ))}
              <button
                type="button"
                onClick={() => setRequestItems((prev) => [...prev, { description: '', amount: '', receipt_url: '' }])}
                className="rounded-lg border border-sand px-3 py-2 text-sm text-cocoa hover:bg-shell"
              >
                Add line
              </button>
            </div>
            <textarea
              value={requestMemo}
              onChange={(event) => setRequestMemo(event.target.value)}
              placeholder="Memo for admins (optional)"
              className="min-h-[80px] w-full rounded-lg border border-sand px-3 py-2 text-sm"
            />
            <div className="flex items-center justify-between text-sm text-cocoa/80">
              <span>Total</span>
              <span className="font-semibold text-cocoa">{formatCurrency(totalRequestAmount)}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={submittingRequest}
                className="rounded-full bg-cocoa px-5 py-2 text-sm font-semibold text-white hover:bg-cocoa/90 disabled:opacity-60"
              >
                {submittingRequest ? 'Submitting…' : 'Submit reimbursement'}
              </button>
              <button
                type="button"
                onClick={resetRequestForm}
                className="rounded-full border border-sand px-5 py-2 text-sm font-semibold text-cocoa hover:bg-shell"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {requestsLoading ? (
          <p className="text-sm text-cocoa/70">Loading reimbursements…</p>
        ) : requestError ? (
          <p className="text-sm text-brick">{requestError}</p>
        ) : requests.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-sand">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-cocoa/60">
                  <th className="px-3 py-2">Request</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Submitted</th>
                  <th className="px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand text-sm text-cocoa/80">
                {requests.map((req) => (
                  <tr key={req.id}>
                    <td className="px-3 py-2 font-mono text-xs">{req.id}</td>
                    <td className="px-3 py-2 capitalize">{req.status}</td>
                    <td className="px-3 py-2">{req.submitted_at ? new Date(req.submitted_at).toLocaleString() : 'Draft'}</td>
                    <td className="px-3 py-2 font-semibold text-cocoa">{formatCurrency(req.total_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No reimbursements yet"
            description="Upload receipts and submit reimbursement requests for admin review."
            action={
              <button
                type="button"
                onClick={() => setShowRequestForm(true)}
                className="rounded-full border border-sand px-5 py-2 text-sm font-semibold text-cocoa hover:bg-shell"
              >
                Start a request
              </button>
            }
          />
        )}
      </Card>
    </div>
  )
}
